import type { ReportRow, ReportPayload } from "../api/reports";
import { Button } from "../components/Button";
import { StatTile } from "../components/StatTile";
import { FindingCard } from "../components/FindingCard";

export default function StructuredReportDetail({
  row,
  payload,
  busy,
  onRegenerate,
}: {
  row: ReportRow;
  payload: ReportPayload;
  busy: boolean;
  onRegenerate: () => void;
}) {
  return (
    <div className="bg-bg-card border border-border-default rounded-2xl p-7">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-[10.5px] font-bold uppercase tracking-widest text-accent-text bg-accent-soft px-2 py-1 rounded">
          Daily report
        </span>
        <span className="text-[12px] font-mono text-text-muted">
          #{row.sourceChannelId} · {row.reportDate}
        </span>
        <span className="text-[12px] text-text-muted">
          compiled {new Date(row.createdAt).toLocaleString()}
        </span>
        <div className="flex-1" />
        <Button variant="primary-dark" disabled={busy} onClick={onRegenerate}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M8 1v3M8 12v3M1 8h3M12 8h3M3 3l2 2M11 11l2 2M3 13l2-2M11 5l2-2"
              stroke="#8FD8BC" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          {busy ? "Working…" : "Regenerate"}
        </Button>
      </div>

      <h2 className="font-display text-[27px] font-semibold leading-tight tracking-tight">
        {payload.headline}
      </h2>
      <p className="mt-3 text-sm leading-[1.65] text-[#3B463F] max-w-[640px]">
        {payload.lede}
      </p>

      <div className="grid grid-cols-3 gap-3 mt-6">
        {payload.stats.map((s) => (
          <StatTile key={s.label} label={s.label} value={s.value} />
        ))}
      </div>

      <div className="mt-8">
        <div className="text-[10px] font-semibold text-text-muted uppercase tracking-widest mb-3">
          Key findings
        </div>
        <div className="flex flex-col gap-3">
          {payload.findings.map((f, i) => (
            <FindingCard key={i} finding={f} />
          ))}
          {payload.findings.length === 0 && (
            <p className="text-[13px] text-text-muted italic">No findings.</p>
          )}
        </div>
      </div>

      <div className="mt-8">
        <div className="text-[10px] font-semibold text-text-muted uppercase tracking-widest mb-3">
          Suggested actions
        </div>
        <ul className="flex flex-col gap-2">
          {payload.actions.map((a, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="w-4 h-4 mt-0.5 border-[1.5px] border-[#C8CEC9] rounded-sm flex-none" />
              <span className="text-[13px] text-text-primary">{a}</span>
            </li>
          ))}
          {payload.actions.length === 0 && (
            <p className="text-[13px] text-text-muted italic">No actions.</p>
          )}
        </ul>
      </div>
    </div>
  );
}
